import type { Auth, MultiFactorResolver, TotpSecret } from 'firebase/auth'
import { setSessionCookie, clearSessionCookie } from '~/utils/sessionCookie'
import { resetPostHogUser } from '~/composables/usePostHog'
import { clearPerUserClientState } from '~/utils/perUserClientState'
import { MODEL_SELECTOR_STATE_KEY } from '~/composables/useModelSelector'
import { DEFAULT_CHAT_TIER, type ChatTier } from '#shared/chat/tiers'
import type { MfaRecoveryCodesResponse, MfaRecoverySignInResponse } from '#shared/auth/mfa'

interface AppUser {
  displayName: string | null
  email: string | null
  uid: string
}

/**
 * MFA sign-in-in-progress state (AlexAmin/marcus#899).
 *
 * `pendingMfaResolver`/`pendingTotpSecret` are NOT `useState` — a `MultiFactorResolver` /
 * `TotpSecret` is a live Firebase SDK object, not serializable app state, and this is client-only
 * interactive flow to begin with (no SSR concern) — so they stay plain module variables.
 *
 * `mfaSignInHint` and `error` ARE template-bound state, so they use `useState` inside `useAuth()`
 * below, alongside `user` and `loading` (AlexAmin/marcus#915 — `useState()` requires an active Nuxt
 * app instance and must never be called at module scope).
 */
let pendingMfaResolver: MultiFactorResolver | null = null
let pendingTotpSecret: TotpSecret | null = null
/** The second-factor challenge a REauthentication raised (MAR-1422), kept so the code completes it without repeating the first factor. */
let pendingReauthResolver: MultiFactorResolver | null = null

/**
 * Auth state + actions.
 *
 * The Firebase SDK is lazy (see app/plugins/firebase.client.ts): `firebase/auth`
 * is dynamically imported and the onIdTokenChanged subscription that maintains
 * `user`/`loading` and the __session cookie lives in the plugin. Actions that
 * need auth await `$ensureFirebaseAuth()`; passive readers (getToken,
 * isEmailAuth) use the late-provided `$firebaseAuth` and degrade to null/false
 * when Firebase hasn't been initialized (public marketing routes).
 */
export function useAuth() {
  const nuxtApp = useNuxtApp()

  const user = useState<AppUser | null>('auth-user', () => null)
  const loading = useState<boolean>('auth-loading', () => true)
  const error = useState<string>('auth-error', () => '')
  const mfaSignInHint = useState<{ factorId: string; displayName: string | null } | null>('auth-mfa-hint', () => null)
  /** Whether Firebase says the email is verified (MAR-950). `null` until `refreshEmailVerification` has asked. */
  const emailVerified = useState<boolean | null>('auth-email-verified', () => null)
  // The chat tier `useModelSelector` holds — reset by `signOut()` (MAR-1458). Taken here, in setup,
  // because `useState` needs the active Nuxt context that a click handler does not have (#915).
  const chatTier = useState<ChatTier>(MODEL_SELECTOR_STATE_KEY, () => DEFAULT_CHAT_TIER)

  /** Passive read — never triggers Firebase initialization. */
  function currentAuth(): Auth | undefined {
    return nuxtApp.$firebaseAuth as Auth | undefined
  }

  /** Active — initializes Firebase (dynamic import) if needed. Client-only. */
  async function requireAuth(): Promise<Auth> {
    const ensure = nuxtApp.$ensureFirebaseAuth as (() => Promise<Auth>) | undefined
    if (!ensure) throw new Error('Firebase Auth not initialized')
    return ensure()
  }

  /**
   * Start App Check before a flow that may create an account (MAR-1214): a new account's first API
   * request has to carry a token (`resolveUserContext` gates the first `users` row on it), and
   * starting reCAPTCHA now lets it observe the form and have a token ready for that retry. Fire and
   * forget; a no-op under the emulator or without a site key.
   */
  function warmAppCheck() {
    const ensure = nuxtApp.$ensureAppCheck as (() => Promise<unknown>) | undefined
    void ensure?.().catch(() => undefined)
  }

  async function finalizeSignInUser(fbUser: {
    displayName: string | null
    email: string | null
    uid: string
    getIdToken: (forceRefresh?: boolean) => Promise<string>
  }) {
    const idToken = await fbUser.getIdToken()
    const claimsRefreshed = await setSessionCookie(idToken)
    user.value = {
      displayName: fbUser.displayName,
      email: fbUser.email,
      uid: fbUser.uid,
    }
    if (claimsRefreshed) {
      await fbUser.getIdToken(true)
    }
  }

  async function captureMfaResolver(auth: Auth, err: unknown) {
    const { getMultiFactorResolver } = await import('firebase/auth')
    const resolver = getMultiFactorResolver(auth, err as any)
    pendingMfaResolver = resolver
    const hint = resolver.hints[0]
    mfaSignInHint.value = hint ? { factorId: hint.factorId, displayName: hint.displayName ?? null } : null
  }

  async function signIn() {
    error.value = ''
    // A Google sign-in may be a first one, which creates the account.
    warmAppCheck()
    try {
      const [auth, { GoogleAuthProvider, signInWithPopup }] = await Promise.all([requireAuth(), import('firebase/auth')])
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      await finalizeSignInUser(result.user)
    } catch (err: any) {
      // MFA-enrolled account (AlexAmin/marcus#899): the popup sign-in succeeded on Google's side but
      // Identity Platform is withholding the ID token pending a second factor. Capture the resolver
      // so the login page can prompt for a TOTP code, same as the email/password path below.
      if (err?.code === 'auth/multi-factor-auth-required') {
        const auth = await requireAuth()
        await captureMfaResolver(auth, err)
        throw err
      }
      error.value = err.message || 'Sign in failed'
      throw err
    }
  }

  async function signInWithEmail(email: string, password: string) {
    error.value = ''
    try {
      const [auth, { signInWithEmailAndPassword }] = await Promise.all([requireAuth(), import('firebase/auth')])
      const result = await signInWithEmailAndPassword(auth, email, password)
      await finalizeSignInUser(result.user)
    } catch (err: any) {
      // The password was CORRECT — Firebase only raises this once the first factor has already
      // passed — and the account has an enrolled second factor. `err` here is the one and only value
      // `getMultiFactorResolver` accepts; capture it before doing anything else with `err`.
      if (err?.code === 'auth/multi-factor-auth-required') {
        const auth = await requireAuth()
        await captureMfaResolver(auth, err)
        throw err
      }
      error.value = err.message || 'Sign in failed'
      throw err
    }
  }

  /**
   * Step 2 of an MFA sign-in: complete the TOTP challenge captured by `captureMfaResolver` above and
   * finish signing in. Throws if there is no sign-in in progress (`signIn`/`signInWithEmail` must
   * have thrown `auth/multi-factor-auth-required` first) or the account's factor isn't TOTP.
   */
  async function completeTotpSignIn(otp: string) {
    if (!pendingMfaResolver) throw new Error('No two-factor sign-in in progress')
    const resolver = pendingMfaResolver
    const hint = resolver.hints.find(h => h.factorId === 'totp') ?? resolver.hints[0]
    if (!hint) throw new Error('No authenticator factor is enrolled for this account')

    const { TotpMultiFactorGenerator } = await import('firebase/auth')
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, otp)
    const result = await resolver.resolveSignIn(assertion)
    pendingMfaResolver = null
    mfaSignInHint.value = null

    await finalizeSignInUser(result.user)
  }

  /** Abandon an in-progress MFA sign-in (e.g. the user goes back to re-enter their password). */
  function cancelMfaSignIn() {
    pendingMfaResolver = null
    mfaSignInHint.value = null
  }

  /**
   * The lost-authenticator fallback (AlexAmin/marcus#899): trade a single-use recovery code for a
   * Firebase custom token via `POST /api/auth/mfa/recovery-signin`, and sign in with it — a path that
   * bypasses the (unavailable) second factor entirely. See that endpoint for the security reasoning.
   */
  async function signInWithRecoveryCode(email: string, recoveryCode: string) {
    error.value = ''
    try {
      const { apiFetch } = useApi()
      const { custom_token: customToken } = await apiFetch<MfaRecoverySignInResponse>('/api/auth/mfa/recovery-signin', {
        method: 'POST',
        body: { email, recovery_code: recoveryCode },
      })
      const [auth, { signInWithCustomToken }] = await Promise.all([requireAuth(), import('firebase/auth')])
      const result = await signInWithCustomToken(auth, customToken)
      await finalizeSignInUser(result.user)
      pendingMfaResolver = null
      mfaSignInHint.value = null
    } catch (err: any) {
      error.value = err.message || 'Sign in failed'
      throw err
    }
  }

  async function signUpWithEmail(email: string, password: string, continueUrl?: string) {
    error.value = ''
    warmAppCheck()
    try {
      const [auth, { createUserWithEmailAndPassword, sendEmailVerification }] = await Promise.all([requireAuth(), import('firebase/auth')])
      const result = await createUserWithEmailAndPassword(auth, email, password)
      // MAR-950: Identity Platform enrols a second factor only for a verified email, so every
      // password account gets its verification email at sign-up. Best-effort: the account exists
      // already, and the security page can send another one on demand, so a failure here (a quota,
      // a network blip) must never turn a successful sign-up into an error.
      // MAR-1216: Landing free-tier sign-up passes `continueUrl` (`${window.location.origin}/#investigar`)
      // so the "Continuar" button on Firebase's hosted action page returns the user to their draft.
      try {
        auth.languageCode = uiLanguage()
        if (continueUrl) {
          await sendEmailVerification(result.user, { url: continueUrl })
        } else {
          await sendEmailVerification(result.user)
        }
        emailVerified.value = false
      } catch (verifyErr) {
        console.warn('[auth] could not send the verification email after sign-up:', verifyErr)
      }
      await finalizeSignInUser(result.user)
    } catch (err: any) {
      error.value = err.message || 'Sign up failed'
      throw err
    }
  }

  /**
   * The UI language, for the emails Firebase sends on our behalf. Its default templates are
   * localised by `auth.languageCode`; left unset they go out in the project's default language.
   */
  function uiLanguage(): string | null {
    const locale = (nuxtApp.$i18n as { locale?: { value?: string } } | undefined)?.locale?.value
    return locale || null
  }

  /**
   * Send the signed-in user a verification email (MAR-950 / MAR-1216). When called from the
   * security page without `continueUrl`, no `ActionCodeSettings` is passed; when called from the
   * landing inline verification state with `continueUrl` (`${window.location.origin}/#investigar`),
   * passes `{ url: continueUrl }` so Firebase's hosted action page links back to `/#investigar`.
   * SDK errors are rethrown for the caller to translate (`auth/too-many-requests` above all).
   */
  async function sendVerificationEmail(continueUrl?: string) {
    const [auth, { sendEmailVerification }] = await Promise.all([requireAuth(), import('firebase/auth')])
    const currentUser = auth.currentUser
    if (!currentUser) throw new Error('No authenticated user')
    auth.languageCode = uiLanguage()
    if (continueUrl) {
      await sendEmailVerification(currentUser, { url: continueUrl })
    } else {
      await sendEmailVerification(currentUser)
    }
  }

  /**
   * Ask Firebase again whether the email is verified — `user.reload()` re-reads the account record,
   * so a link opened in another tab shows up here without a re-login (MAR-950). When the answer has
   * just flipped to verified, the ID token still says `email_verified: false`; it is force-refreshed
   * so the next call Identity Platform checks (TOTP enrolment) carries the new claim. A failed reload
   * falls back to the SDK's cached flag rather than throwing — the page must render either way.
   *
   * `forceTokenRefresh` is for a caller acting on a server `unverified` answer (`/chat`'s guard, the
   * landing's verify poll): that answer IS the evidence the token is stale, so a verified user's token
   * is refreshed even when the flag was already true before this call — verified in another tab, or
   * reloaded by an earlier poll. Without it the flip was the only trigger, and a user who missed it
   * kept `email_verified: false` and bounced between `/chat` and the verify page (MAR-1465).
   */
  async function refreshEmailVerification(opts: { forceTokenRefresh?: boolean } = {}): Promise<boolean> {
    const auth = await requireAuth()
    const currentUser = auth.currentUser
    if (!currentUser) {
      emailVerified.value = null
      return false
    }
    const before = currentUser.emailVerified
    try {
      await currentUser.reload()
    } catch (err) {
      console.warn('[auth] could not reload the user to re-check email verification:', err)
    }
    const verified = currentUser.emailVerified
    if (verified && (!before || opts.forceTokenRefresh)) {
      try {
        await currentUser.getIdToken(true)
      } catch (err) {
        console.warn('[auth] could not refresh the ID token after email verification:', err)
      }
    }
    emailVerified.value = verified
    return verified
  }

  /**
   * Passive: whether Firebase's signed-in account is a real one whose email is NOT verified yet
   * (MAR-1465). `/chat` refuses such a user and bounces them to `LANDING_VERIFY_EMAIL_PATH`, so the
   * routes that would send them there (post-login, the navbar's "Ir al dashboard") send them to the
   * verify page directly. False when Firebase is not initialised, signed out or anonymous. The SDK's
   * cached flag can lag a verification made elsewhere; that user lands on the verify page, whose poll
   * reloads and routes them on.
   */
  function isEmailUnverified(): boolean {
    const currentUser = currentAuth()?.currentUser
    if (!currentUser || currentUser.isAnonymous) return false
    return currentUser.emailVerified === false
  }

  async function resetPassword(email: string) {
    error.value = ''
    try {
      const [auth, { sendPasswordResetEmail }] = await Promise.all([requireAuth(), import('firebase/auth')])
      await sendPasswordResetEmail(auth, email)
    } catch (err: any) {
      error.value = err.message || 'Failed to send reset email'
      throw err
    }
  }

  /**
   * `redirectTo` is where the browser lands afterwards (default `/login`); the landing navbar passes `/`
   * so a visitor who signs out there stays on the public site (MAR-1406). Anything that is not an
   * options object (a stray click event handed straight to this function) is ignored.
   */
  async function signOut(opts?: { redirectTo?: string }) {
    const redirectTo = opts && typeof opts.redirectTo === 'string' ? opts.redirectTo : '/login'
    // Clear local state + cookie up front so the UI can never show a stale
    // logged-in state, even if Firebase is slow or unavailable. clearSessionCookie
    // hits the server (the __session cookie is HttpOnly — a client write can't
    // remove it).
    user.value = null
    resetPostHogUser()
    // Web storage belongs to the browser, not the account: without this the NEXT account to sign in
    // here inherits the last one's chat tier, chat scope, org override and drafts (MAR-1458). The
    // in-memory tier is reset too, for anything that reads it before the navigation below lands.
    clearPerUserClientState()
    chatTier.value = DEFAULT_CHAT_TIER
    await clearSessionCookie()
    try {
      // requireAuth() (not currentAuth()) so we INITIALIZE Firebase if it isn't
      // already — otherwise fbSignOut never runs and the PERSISTENT session
      // (IndexedDB) survives, to be restored on the next route that loads the
      // SDK (→ "authed" UI with no cookie). Signing out clears that persistence.
      const [auth, { signOut: fbSignOut }] = await Promise.all([requireAuth(), import('firebase/auth')])
      await fbSignOut(auth)
    } catch {
      // Firebase unavailable — local state + cookie are already cleared.
    }
    window.location.href = redirectTo
  }

  async function getToken(): Promise<string | null> {
    const auth = currentAuth()
    if (!auth) return null
    const currentUser = auth.currentUser
    // Anonymous = logged-out for the app: never hand an anon token to the authed API
    // (the server rejects it anyway). The /research page reads its anon token via
    // useResearchSession, not here. This is the single app's isolation guarantee.
    if (!currentUser || currentUser.isAnonymous) return null
    try {
      return await currentUser.getIdToken()
    } catch (err) {
      // Mirrors refreshIdToken()'s guard in app/plugins/api.ts: a transient Firebase/network hiccup
      // here must not surface as a raw FirebaseError out of the auth layer — every caller of
      // getToken() (the $api Authorization header, chat auth, etc.) would otherwise see an
      // uncaught rejection instead of "no token available right now".
      console.warn('[auth] failed to get ID token:', err)
      return null
    }
  }

  /** Wait until Firebase Auth has restored the session (or confirmed no user). */
  async function waitForAuth(): Promise<void> {
    if (import.meta.server) return
    const wait = nuxtApp.$waitForAuth as (() => Promise<void>) | undefined
    if (typeof wait === 'function') {
      await wait()
      return
    }
    const ensure = nuxtApp.$ensureFirebaseAuth as (() => Promise<unknown>) | undefined
    if (typeof ensure === 'function') {
      await ensure()
    }
  }

  const isEmailAuth = computed(() => {
    const auth = currentAuth()
    if (!auth?.currentUser) return false
    return auth.currentUser.providerData.some(p => p.providerId === 'password')
  })

  async function changePassword(currentPassword: string, newPassword: string, otp?: string) {
    const [auth, { updatePassword }] = await Promise.all([requireAuth(), import('firebase/auth')])
    if (!auth.currentUser?.email) throw new Error('No authenticated user')
    await reauthenticate(currentPassword, otp)
    await updatePassword(auth.currentUser, newPassword)
  }

  /**
   * Reauthenticate the current user in place for a sensitive operation Identity Platform is refusing
   * with `auth/requires-recent-login` (AlexAmin/marcus#899 follow-up to the TOTP enrollment flow
   * below). Same "recently signed in" pattern `disableMfa` already uses: a password credential for an
   * email/password account, a fresh Google popup otherwise — kept as its own function so
   * `security.vue` can call it directly and then retry whichever enrollment step failed, instead of
   * routing the user through a full sign-out/sign-in round trip.
   *
   * The one reauthentication helper `changePassword` and `disableMfa` go through too (MAR-1422). For
   * a TOTP-enrolled account Firebase answers the first factor with `auth/multi-factor-auth-required`;
   * that error is rethrown as the "needs a code" signal, with its resolver kept in
   * `pendingReauthResolver`. Calling again with `otp` completes THAT challenge — same
   * `assertionForSignIn` → `resolveSignIn` as `completeTotpSignIn` — without repeating the first
   * factor (no second popup). A wrong code is rethrown and the challenge stays open for another try.
   */
  async function reauthenticate(currentPassword?: string, otp?: string) {
    const [auth, authMod] = await Promise.all([requireAuth(), import('firebase/auth')])
    const currentUser = auth.currentUser
    if (!currentUser) throw new Error('No authenticated user')

    const resolveWithCode = async (resolver: MultiFactorResolver, code: string) => {
      const hint = resolver.hints.find(h => h.factorId === 'totp') ?? resolver.hints[0]
      if (!hint) throw new Error('No authenticator factor is enrolled for this account')
      await resolver.resolveSignIn(authMod.TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code))
      pendingReauthResolver = null
    }

    if (otp && pendingReauthResolver) {
      await resolveWithCode(pendingReauthResolver, otp)
      return
    }

    pendingReauthResolver = null
    try {
      if (isEmailAuth.value) {
        if (!currentUser.email || !currentPassword) throw new Error('Current password is required')
        const credential = authMod.EmailAuthProvider.credential(currentUser.email, currentPassword)
        await authMod.reauthenticateWithCredential(currentUser, credential)
      } else {
        await authMod.reauthenticateWithPopup(currentUser, new authMod.GoogleAuthProvider())
      }
    } catch (err: any) {
      if (err?.code !== 'auth/multi-factor-auth-required') throw err
      pendingReauthResolver = authMod.getMultiFactorResolver(auth, err)
      if (!otp) throw err
      await resolveWithCode(pendingReauthResolver, otp)
    }
  }

  /**
   * Step 1 of TOTP enrollment: mint a new secret from Firebase and the otpauth:// URL to render as a
   * QR code. Nothing is persisted — server-side or Firebase-side — until `finishTotpEnrollment`
   * presents a valid code back.
   *
   * `multiFactor(currentUser).getSession()` (and `finishTotpEnrollment`'s `.enroll()` below) are the
   * two calls Firebase's own docs name as throwing `auth/requires-recent-login` — MFA enrollment is a
   * "sensitive" account operation Identity Platform gates on a fresh sign-in, same as
   * `changePassword`'s `updatePassword` and `disableMfa`'s unenroll. `security.vue` handles that by
   * calling `reauthenticate()` above in place and retrying this function (AlexAmin/marcus#899
   * follow-up) — it no longer routes the user through `signOut()`, which stays only as a fallback for
   * when reauthentication itself fails.
   */
  async function startTotpEnrollment(): Promise<{ secretKey: string; qrCodeUrl: string }> {
    const [auth, { multiFactor, TotpMultiFactorGenerator }] = await Promise.all([requireAuth(), import('firebase/auth')])
    const currentUser = auth.currentUser
    if (!currentUser) throw new Error('No authenticated user')

    const session = await multiFactor(currentUser).getSession()
    const secret = await TotpMultiFactorGenerator.generateSecret(session)
    pendingTotpSecret = secret

    const accountName = currentUser.email || currentUser.uid
    const qrCodeUrl = secret.generateQrCodeUrl(accountName, 'Marcus')
    return { secretKey: secret.secretKey, qrCodeUrl }
  }

  /**
   * Step 2: the user types the six-digit code their authenticator app produced from the secret
   * `startTotpEnrollment` gave them. This is what actually registers the factor with Firebase.
   * Marcus's own `mfa_enrolled_at` is set — and recovery codes are minted — only AFTER this succeeds,
   * by `POST /api/auth/mfa/enroll` (`server/api/auth/mfa/enroll.post.ts`), which re-verifies the
   * enrollment with the Admin SDK rather than trusting this call.
   *
   * Returns the recovery codes, in plaintext, exactly once — the caller (`security.vue`) must show
   * them to the user immediately and never hold onto them after that.
   */
  async function finishTotpEnrollment(otp: string): Promise<string[]> {
    const [auth, { multiFactor, TotpMultiFactorGenerator }] = await Promise.all([requireAuth(), import('firebase/auth')])
    const currentUser = auth.currentUser
    if (!currentUser || !pendingTotpSecret) throw new Error('No enrollment in progress')

    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(pendingTotpSecret, otp)
    await multiFactor(currentUser).enroll(assertion, 'Authenticator app')
    pendingTotpSecret = null

    const { apiFetch } = useApi()
    const { recovery_codes: recoveryCodes } = await apiFetch<MfaRecoveryCodesResponse>('/api/auth/mfa/enroll', { method: 'POST' })
    return recoveryCodes
  }

  /** Abandon an in-progress enrollment (e.g. the user closes the dialog before entering a code). */
  function cancelTotpEnrollment() {
    pendingTotpSecret = null
  }

  /**
   * Turn MFA off. Reauthenticates first — password for an email/password account, a fresh Google
   * popup otherwise, plus the TOTP code (`otp`) the enrolled factor demands (MAR-1422) — the same
   * "recently signed in" requirement `changePassword` already has to satisfy, then tells the server (`POST /api/auth/mfa/disable`), which is what actually clears
   * `users.mfa_enrolled_at` and removes the factor from the Firebase user record via the Admin SDK.
   */
  async function disableMfa(currentPassword?: string, otp?: string) {
    const [auth, authMod] = await Promise.all([requireAuth(), import('firebase/auth')])
    const currentUser = auth.currentUser
    if (!currentUser) throw new Error('No authenticated user')

    // An enrolled user's reauthentication raises the second-factor challenge; see `reauthenticate`.
    await reauthenticate(currentPassword, otp)

    const { apiFetch } = useApi()
    await apiFetch('/api/auth/mfa/disable', { method: 'POST' })

    // Best-effort against Firebase directly too, so the SDK's local enrolled-factor state updates
    // immediately instead of waiting for a token refresh. The server call above already did the half
    // that matters for enforcement; whether MFA is on, for the settings UI, is read from
    // `GET /api/auth/mfa/status` (Marcus's own `mfa_enrolled_at`) rather than mirrored here, because
    // `multiFactor()` is an async-import-only function and Marcus's DB flag is what enforcement
    // actually keys on — the two must never disagree, so there is exactly one place that answers
    // "is this user enrolled".
    try {
      for (const factor of authMod.multiFactor(currentUser).enrolledFactors) {
        await authMod.multiFactor(currentUser).unenroll(factor)
      }
    } catch {
      // Server-side disable already succeeded; this is a local-state nicety only.
    }
  }

  return {
    user,
    loading,
    error,
    isEmailAuth,
    mfaSignInHint,
    emailVerified,
    signIn,
    signInWithEmail,
    signUpWithEmail,
    sendVerificationEmail,
    refreshEmailVerification,
    isEmailUnverified,
    resetPassword,
    changePassword,
    signOut,
    getToken,
    waitForAuth,
    completeTotpSignIn,
    cancelMfaSignIn,
    signInWithRecoveryCode,
    reauthenticate,
    startTotpEnrollment,
    finishTotpEnrollment,
    cancelTotpEnrollment,
    disableMfa,
  }
}
